import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Edit, Trash2, Calendar, Images } from 'lucide-react';
import { Collection, deleteCollection } from '@/utils/collectionsGraphQL';
import { EditCollectionModal } from './EditCollectionModal';
import { DeleteCollectionDialog } from './DeleteCollectionDialog';
import { useToast } from '@/hooks/use-toast';

interface CollectionCardProps {
  collection: Collection;
  onDeleteSuccess?: () => void;
}

export const CollectionCard: React.FC<CollectionCardProps> = ({ 
  collection, 
  onDeleteSuccess 
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const formatDate = (dateString: string) => {
    try {
      // Handle Unix timestamps (both seconds and milliseconds)
      const timestamp = parseInt(dateString);
      if (!isNaN(timestamp)) {
        // If timestamp is less than 1e12, it's likely in seconds, multiply by 1000 for milliseconds
        const date = new Date(timestamp < 1e12 ? timestamp * 1000 : timestamp);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      
      // Handle ISO date strings
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      
      return 'Invalid date';
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Invalid date';
    }
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    // Parent will refetch data
    if (onDeleteSuccess) onDeleteSuccess();
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteCollection(collection.id);
      setIsDeleteDialogOpen(false);
      if (onDeleteSuccess) onDeleteSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete collection",
        variant: "destructive",
      });
    }
  };

  const handleCardClick = () => {
    // Don't navigate if dropdown menu is open
    if (isDropdownMenuOpen) {
      return;
    }
    navigate(`/collections/${collection.id}`);
  };

  const assetCount = collection.assets?.length || 0;

  return (
    <>
      <Card
        className="hover:shadow-md hover:bg-gray-50 transition-all overflow-hidden"
      >
        {/* Thumbnail Image or Placeholder */}
        <div
          className="w-full h-48 bg-gray-200 cursor-pointer overflow-hidden flex items-center justify-center"
          onClick={handleCardClick}
        >
          {collection.canva_design_thumbnail ? (
            <img
              src={collection.canva_design_thumbnail}
              alt={collection.name}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
            />
          ) : (
            <Images className="w-16 h-16 text-gray-400" />
          )}
        </div>

        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1 flex-1">
              <CardTitle
                className="text-lg font-semibold line-clamp-1 cursor-pointer hover:text-blue-600 transition-colors"
                onClick={handleCardClick}
              >
                {collection.name}
              </CardTitle>
              <CardDescription
                className="text-sm text-gray-600 line-clamp-2 h-10 cursor-pointer hover:text-gray-800 transition-colors"
                onClick={handleCardClick}
              >
                {collection.description || 'No description available'}
              </CardDescription>
            </div>
            <DropdownMenu onOpenChange={setIsDropdownMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 w-8 p-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsEditModalOpen(true);
                }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="text-red-600"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteClick();
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>

        <CardContent>
          <div className="space-y-3">
            {/* Asset Count */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Assets</span>
              <Badge variant="secondary">
                {assetCount} {assetCount === 1 ? 'asset' : 'assets'}
              </Badge>
            </div>

            {/* Created Date */}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="h-4 w-4" />
              <span>Created {formatDate(collection.created_at)}</span>
            </div>
          </div>
        </CardContent>

      </Card>

      {/* Edit Modal */}
      <EditCollectionModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        collection={collection}
        onSuccess={handleEditSuccess}
      />

      {/* Delete Dialog */}
      <DeleteCollectionDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        collection={collection}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
};